import { useState, useCallback } from 'react';
import { getApiBaseUrl, createApiHeaders, apiFetch } from '../../../config/api';
import { REQUEST_TIMEOUT_LLM_EXECUTION } from '../../../config/constants';

/**
 * Custom hook for executing templates using the advanced API endpoint
 * Supports draft version execution and content check integration
 *
 * @param {string} templateId - Template ID
 * @param {number} currentVersionNumber - Current version number being edited
 * @param {string} templateName - Optional template name for logging
 * @returns {Object} Execution state and functions
 */
export const useTemplateExecution = (
  templateId,
  currentVersionNumber,
  templateName = null
) => {
  const [executing, setExecuting] = useState(false);
  const [executionResult, setExecutionResult] = useState(null);
  const [error, setError] = useState(null);

  /**
   * Execute template using advanced endpoint with draft support
   * @param {Object} inputData - Input data for template variables
   * @param {Object} options - Additional options (llm_config overrides, etc.)
   * @returns {Promise<Object>} Execution result
   */
  const executeTemplate = useCallback(
    async (inputData, options = {}) => {
      if (!templateId) {
        throw new Error('Template ID is required');
      }

      try {
        setExecuting(true);
        setError(null);
        setExecutionResult(null);

        const headers = createApiHeaders();

        const payload = {
          template_id: templateId,
          template_name: templateName || undefined,
          input_data: inputData,
          version_number: currentVersionNumber || undefined,
          allow_draft: true, // Always allow draft in editor context
          async_mode: true, // Use async mode to avoid API Gateway timeout
          ...options,
        };

        // Remove undefined values
        Object.keys(payload).forEach(
          key => payload[key] === undefined && delete payload[key]
        );

        // Create async job - use apiFetch with 300 second timeout for template execution
        const jobResponse = await apiFetch(
          `/api/v1/templates/advanced/execute`,
          {
            method: 'POST',
            headers,
            body: JSON.stringify(payload),
            timeout: REQUEST_TIMEOUT_LLM_EXECUTION, // 300 seconds for template execution
          }
        );

        // Check if async mode was used (has job_id)
        if (jobResponse.job_id) {
          // Async mode: Poll for job status with fixed 3 second interval (no exponential backoff)
          const jobId = jobResponse.job_id;
          const pollDelay = 3000; // Fixed 3 seconds interval
          const maxPollTime = 330000; // 5.5 minutes (330 seconds) - matches backend job timeout of 300s + 30s buffer
          const startTime = Date.now();

          while (Date.now() - startTime < maxPollTime) {
            await new Promise(resolve => setTimeout(resolve, pollDelay));

            const statusResponse = await fetch(
              `${getApiBaseUrl()}/api/v1/templates/advanced/jobs/${jobId}/status`,
              { headers }
            );

            if (!statusResponse.ok) {
              throw new Error(
                `Failed to get job status: ${statusResponse.status}`
              );
            }

            const statusData = await statusResponse.json();

            if (statusData.status === 'completed') {
              const result = statusData.result;
              setExecutionResult(result);
              setExecuting(false);
              return result;
            } else if (
              statusData.status === 'failed' ||
              statusData.status === 'timeout'
            ) {
              throw new Error(statusData.error || `Job ${statusData.status}`);
            }

            // Fixed interval - continue polling with 3 second delay
          }

          throw new Error('Job polling timeout - request took too long');
        } else {
          // Synchronous mode (fallback)
          setExecutionResult(jobResponse);
          setExecuting(false);
          return jobResponse;
        }
      } catch (err) {
        const errorMessage =
          err.message || 'An error occurred during template execution';
        setError(errorMessage);
        setExecuting(false);
        throw err;
      }
    },
    [templateId, currentVersionNumber, templateName]
  );

  /**
   * Clear execution result and error
   */
  const clearResult = useCallback(() => {
    setExecutionResult(null);
    setError(null);
  }, []);

  return {
    executing,
    executionResult,
    error,
    executeTemplate,
    clearResult,
  };
};
